// src/pages/Notifications.js - Sistema de Push Notifications
import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Card, Row, Col, Statistic, Button, Form, Input,
  Select, DatePicker, Radio, Table, Tag, Space, Tabs, Tooltip,
  message, Spin, Popconfirm, Divider, Empty, Badge, Progress, TimePicker, Modal
} from 'antd';
import {
  BellOutlined, SendOutlined, ClockCircleOutlined, AndroidOutlined,
  AppleOutlined, GlobalOutlined, MobileOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ExclamationCircleOutlined, DeleteOutlined,
  ReloadOutlined, LinkOutlined, PictureOutlined, HistoryOutlined,
  TeamOutlined, RocketOutlined, ScheduleOutlined, StopOutlined,
  InfoCircleOutlined, ThunderboltOutlined, PlusOutlined, EditOutlined
} from '@ant-design/icons';
import { notificationsAPI, articlesAPI } from '../utils/api';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const { Title, Text } = Typography;
const { TextArea } = Input;

// ============================================
// Utilidades
// ============================================

const slugify = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const platformIcons = {
  all: <MobileOutlined />,
  android: <AndroidOutlined style={{ color: '#3DDC84' }} />,
  ios: <AppleOutlined style={{ color: '#000' }} />,
  web: <GlobalOutlined style={{ color: '#1890ff' }} />,
};

const platformLabels = {
  all: 'Todos',
  android: 'Android',
  ios: 'iOS',
  web: 'Web/PWA',
};

const statusConfig = {
  sent: { color: 'success', icon: <CheckCircleOutlined />, label: 'Enviada' },
  scheduled: { color: 'processing', icon: <ClockCircleOutlined />, label: 'Programada' },
  cancelled: { color: 'default', icon: <StopOutlined />, label: 'Cancelada' },
  failed: { color: 'error', icon: <CloseCircleOutlined />, label: 'Fallida' },
  pending: { color: 'warning', icon: <ExclamationCircleOutlined />, label: 'Pendiente' },
};

// ============================================
// Componente: Vista previa de la notificación
// ============================================

const NotificationPreview = ({ title, body, imageUrl }) => {
  if (!title && !body) {
    return (
      <div style={{
        padding: 40, textAlign: 'center', color: '#bfbfbf',
        border: '2px dashed #f0f0f0', borderRadius: 12, background: '#fafafa'
      }}>
        <BellOutlined style={{ fontSize: 32, marginBottom: 8 }} />
        <div>Escribe un título y mensaje para ver la vista previa</div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#f8f9fa', borderRadius: 16, padding: 16,
      maxWidth: 340, margin: '0 auto',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: '1px solid #e8e8e8'
    }}>
      {/* Cabecera tipo notificación móvil */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: 5,
          background: 'linear-gradient(135deg, #c82318, #e74c3c)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <BellOutlined style={{ color: 'white', fontSize: 10 }} />
        </div>
        <Text style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 500 }}>
          AGUSTINOS
        </Text>
        <Text style={{ fontSize: 11, color: '#bfbfbf', marginLeft: 'auto' }}>
          ahora
        </Text>
      </div>

      {/* Contenido */}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2, color: '#262626' }}>
            {title || 'Título de la notificación'}
          </div>
          <div style={{ fontSize: 13, color: '#595959', lineHeight: 1.4 }}>
            {body ? (body.length > 100 ? body.substring(0, 100) + '...' : body) : 'Cuerpo del mensaje...'}
          </div>
        </div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt="preview"
            style={{
              width: 48, height: 48, borderRadius: 8,
              objectFit: 'cover', flexShrink: 0
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>
    </div>
  );
};

// ============================================
// Componente principal
// ============================================

const Notifications = () => {
  // Estado general
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('send');

  // Estado del formulario
  const [form] = Form.useForm();
  const [sendType, setSendType] = useState('immediate');
  const [sending, setSending] = useState(false);
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [previewImage, setPreviewImage] = useState('');

  // Estado del historial
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifPagination, setNotifPagination] = useState({ page: 1, limit: 15, total: 0 });
  const [statusFilter, setStatusFilter] = useState(null);

  // Estado de dispositivos
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesPagination, setDevicesPagination] = useState({ page: 1, limit: 15, total: 0 });

  // Estado de auto-rules
  const [autoRules, setAutoRules] = useState([]);
  const [autoRulesLoading, setAutoRulesLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [newRuleSection, setNewRuleSection] = useState(null);
  const [newRuleTarget, setNewRuleTarget] = useState('all');
  const [newRuleSendTime, setNewRuleSendTime] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [editTarget, setEditTarget] = useState('all');
  const [editSendTime, setEditSendTime] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [platformFilter, setPlatformFilter] = useState(null);

  // ============================================
  // Carga de datos
  // ============================================

  const loadStats = useCallback(async () => {
    try {
      const res = await notificationsAPI.getStats();
      if (res.success) {
        setStats(res.data);
      }
    } catch (error) {
      console.error('Error cargando stats:', error);
    }
  }, []);

  const loadArticles = useCallback(async () => {
    try {
      const res = await articlesAPI.getArticles(null, 900);
      setArticles(res.data || []);
      // Extraer secciones únicas para el selector de auto-rules
      const uniqueSections = [...new Set((res.data || []).map(a => a.section).filter(Boolean))].sort();
      setSections(uniqueSections);
    } catch (error) {
      console.error('Error cargando artículos:', error);
    }
  }, []);

  const loadAutoRules = useCallback(async () => {
    setAutoRulesLoading(true);
    try {
      const res = await notificationsAPI.getAutoRules();
      if (res.success) {
        setAutoRules(res.data || []);
      }
    } catch (error) {
      console.error('Error cargando auto-rules:', error);
    } finally {
      setAutoRulesLoading(false);
    }
  }, []);

  const loadNotifications = useCallback(async (page = 1, status = null) => {
    setNotifLoading(true);
    try {
      const res = await notificationsAPI.getNotifications(page, 15, status);
      if (res.success) {
        setNotifications(res.data || []);
        setNotifPagination(prev => ({
          ...prev,
          page,
          total: res.pagination?.total || 0,
        }));
      }
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
      message.error('Error al cargar el historial');
    } finally {
      setNotifLoading(false);
    }
  }, []);

  const loadDevices = useCallback(async (page = 1, platform = null) => {
    setDevicesLoading(true);
    try {
      const res = await notificationsAPI.getDevices(page, 15, platform);
      if (res.success) {
        setDevices(res.data || []);
        setDevicesPagination(prev => ({
          ...prev,
          page,
          total: res.pagination?.total || 0,
        }));
      }
    } catch (error) {
      console.error('Error cargando dispositivos:', error);
      message.error('Error al cargar los dispositivos');
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadArticles()]);
      setLoading(false);
    };
    init();
  }, [loadStats, loadArticles]);

  // Cargar datos cuando cambia de tab
  useEffect(() => {
    if (activeTab === 'history') {
      loadNotifications(1, statusFilter);
    } else if (activeTab === 'devices') {
      loadDevices(1, platformFilter);
    } else if (activeTab === 'automation') {
      loadAutoRules();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Handlers del formulario
  // ============================================

  const handleArticleSelect = (articleId) => {
    if (!articleId) {
      setSelectedArticle(null);
      return;
    }
    const article = articles.find(a => a.id === articleId);
    setSelectedArticle(article);

    // Auto-rellenar imagen y subtítulo del artículo vinculado
    if (article) {
      if (article.imageUrl) {
        form.setFieldsValue({ imageUrl: article.imageUrl });
        setPreviewImage(article.imageUrl);
      }
      if (article.subtitle && !form.getFieldValue('body')) {
        form.setFieldsValue({ body: article.subtitle });
        setPreviewBody(article.subtitle);
      }
      if (!form.getFieldValue('title')) {
        form.setFieldsValue({ title: article.title });
        setPreviewTitle(article.title);
      }
    }
  };

  const handleSendNotification = async (values) => {
    setSending(true);
    try {
      // Construir data de deep linking si hay artículo vinculado
      let deepLinkData = {};
      if (selectedArticle) {
        const sectionSlug = slugify(selectedArticle.section);
        const articleSlug = slugify(selectedArticle.title);
        deepLinkData = {
          articleId: String(selectedArticle.id),
          section: sectionSlug,
          slug: articleSlug,
          url: `/${sectionSlug}/${selectedArticle.id}/${articleSlug}`,
        };
      }

      const payload = {
        title: values.title,
        body: values.body,
        imageUrl: values.imageUrl || null,
        articleId: selectedArticle?.id || null,
        target: values.target || 'all',
        data: deepLinkData,
      };

      let res;
      if (sendType === 'scheduled') {
        payload.scheduledAt = values.scheduledAt.toISOString();
        res = await notificationsAPI.scheduleNotification(payload);
      } else {
        res = await notificationsAPI.sendNotification(payload);
      }

      if (res.success) {
        message.success(
          sendType === 'scheduled'
            ? `Notificación programada correctamente`
            : `Notificación enviada a ${res.data?.successCount || 0} dispositivos`
        );
        form.resetFields();
        setSelectedArticle(null);
        setSendType('immediate');
        setPreviewTitle('');
        setPreviewBody('');
        setPreviewImage('');
        loadStats();
      } else {
        message.error(res.message || 'Error al enviar la notificación');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al enviar la notificación';
      message.error(errorMsg);
      console.error('Error enviando notificación:', error);
    } finally {
      setSending(false);
    }
  };

  const handleCancelNotification = async (id) => {
    try {
      const res = await notificationsAPI.cancelNotification(id);
      if (res.success) {
        message.success('Notificación cancelada');
        loadNotifications(notifPagination.page, statusFilter);
        loadStats();
      }
    } catch (error) {
      message.error('Error al cancelar la notificación');
    }
  };

  // ============================================
  // Handlers de auto-rules
  // ============================================

  const handleCreateAutoRule = async () => {
    if (!newRuleSection) {
      message.warning('Selecciona una sección');
      return;
    }
    try {
      const res = await notificationsAPI.createAutoRule({
        section: newRuleSection,
        target: newRuleTarget,
        sendTime: newRuleSendTime ? newRuleSendTime.format('HH:mm') : null,
      });
      if (res.success) {
        message.success(res.message);
        setNewRuleSection(null);
        setNewRuleTarget('all');
        setNewRuleSendTime(null);
        loadAutoRules();
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al crear la regla';
      message.error(msg);
    }
  };

  const handleToggleAutoRule = async (rule) => {
    try {
      const res = await notificationsAPI.updateAutoRule(rule.id, { enabled: !rule.enabled });
      if (res.success) {
        message.success(`Regla ${!rule.enabled ? 'activada' : 'desactivada'}`);
        loadAutoRules();
      }
    } catch (error) {
      message.error('Error al actualizar la regla');
    }
  };

  const handleOpenEditRule = (rule) => {
    setEditingRule(rule);
    setEditTarget(rule.target || 'all');
    setEditSendTime(rule.send_time ? dayjs(rule.send_time, 'HH:mm:ss') : null);
  };

  const handleSaveEditRule = async () => {
    if (!editingRule) return;
    setEditSaving(true);
    try {
      const res = await notificationsAPI.updateAutoRule(editingRule.id, {
        target: editTarget,
        sendTime: editSendTime ? editSendTime.format('HH:mm') : null,
      });
      if (res.success) {
        message.success('Regla actualizada');
        setEditingRule(null);
        loadAutoRules();
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al actualizar la regla';
      message.error(msg);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteAutoRule = async (id) => {
    try {
      const res = await notificationsAPI.deleteAutoRule(id);
      if (res.success) {
        message.success(res.message);
        loadAutoRules();
      }
    } catch (error) {
      message.error('Error al eliminar la regla');
    }
  };

  // ============================================
  // Columnas de tablas
  // ============================================

  const notificationColumns = [
    {
      title: 'Notificación',
      key: 'notification',
      render: (_, record) => (
        <div>
          <Text strong style={{ display: 'block', marginBottom: 2 }}>{record.title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.body?.length > 80 ? record.body.substring(0, 80) + '...' : record.body}
          </Text>
          {record.article_title && (
            <div style={{ marginTop: 4 }}>
              <Tag icon={<LinkOutlined />} color="blue" style={{ fontSize: 11 }}>
                {record.article_title}
              </Tag>
            </div>
          )}
        </div>
      ),
      width: '35%',
    },
    {
      title: 'Destinatarios',
      dataIndex: 'target',
      key: 'target',
      width: 120,
      render: (target) => (
        <Space size={4}>
          {platformIcons[target]}
          <span>{platformLabels[target]}</span>
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'Envíos',
      key: 'delivery',
      width: 100,
      render: (_, record) => {
        if (record.status !== 'sent') return '-';
        const total = (record.success_count || 0) + (record.failure_count || 0);
        if (total === 0) return '-';
        return (
          <Tooltip title={`${record.success_count} éxitos, ${record.failure_count} fallos`}>
            <Progress
              percent={Math.round((record.success_count / total) * 100)}
              size="small"
              status={record.failure_count > 0 ? 'exception' : 'success'}
              format={() => `${record.success_count}/${total}`}
              style={{ width: 80 }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Fecha',
      key: 'date',
      width: 160,
      render: (_, record) => {
        const date = record.sent_at || record.scheduled_at || record.created_at;
        return (
          <div>
            <Text style={{ fontSize: 13 }}>
              {dayjs(date).format('DD MMM YYYY')}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(date).format('HH:mm')}
            </Text>
          </div>
        );
      },
      sorter: (a, b) => {
        const dateA = a.sent_at || a.scheduled_at || a.created_at;
        const dateB = b.sent_at || b.scheduled_at || b.created_at;
        return dayjs(dateA).valueOf() - dayjs(dateB).valueOf();
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        if (record.status === 'scheduled') {
          return (
            <Popconfirm
              title="¿Cancelar esta notificación programada?"
              onConfirm={() => handleCancelNotification(record.id)}
              okText="Sí, cancelar"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                Cancelar
              </Button>
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  const deviceColumns = [
    {
      title: 'Plataforma',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      render: (platform) => (
        <Space size={6}>
          {platformIcons[platform]}
          <Tag color={platform === 'android' ? 'green' : platform === 'ios' ? 'default' : 'blue'}>
            {platformLabels[platform]}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Usuario',
      key: 'user',
      width: 150,
      render: (_, record) => record.user_name || <Text type="secondary">Anónimo</Text>,
    },
    {
      title: 'Info del Dispositivo',
      dataIndex: 'device_info',
      key: 'device_info',
      render: (info) => {
        if (!info || Object.keys(info).length === 0) {
          return <Text type="secondary">Sin información</Text>;
        }
        const model = info.model || info.manufacturer || '';
        const os = info.osVersion || info.os || '';
        return (
          <div>
            {model && <Text style={{ fontSize: 13 }}>{model}</Text>}
            {os && <><br /><Text type="secondary" style={{ fontSize: 12 }}>{os}</Text></>}
          </div>
        );
      },
    },
    {
      title: 'Última actividad',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 140,
      render: (date) => (
        <Tooltip title={dayjs(date).format('DD/MM/YYYY HH:mm:ss')}>
          <Text style={{ fontSize: 13 }}>{dayjs(date).format('DD MMM YYYY')}</Text>
        </Tooltip>
      ),
      sorter: (a, b) => dayjs(a.updated_at).valueOf() - dayjs(b.updated_at).valueOf(),
    },
    {
      title: 'Registrado',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {dayjs(date).format('DD MMM YYYY')}
        </Text>
      ),
    },
  ];

  // ============================================
  // Render tabs
  // ============================================

  const renderSendTab = () => (
    <Row gutter={24}>
      {/* Formulario */}
      <Col xs={24} lg={14}>
        <Card
          title={
            <Space>
              <SendOutlined />
              <span>Nueva Notificación</span>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSendNotification}
            initialValues={{ target: 'all' }}
            size="large"
          >
            <Form.Item
              name="title"
              label="Título"
              rules={[
                { required: true, message: 'El título es obligatorio' },
                { max: 100, message: 'Máximo 100 caracteres' },
              ]}
            >
              <Input
                placeholder="Título de la notificación"
                showCount
                maxLength={100}
                onChange={(e) => setPreviewTitle(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              name="body"
              label="Mensaje"
              rules={[
                { required: true, message: 'El mensaje es obligatorio' },
                { max: 500, message: 'Máximo 500 caracteres' },
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Cuerpo del mensaje de la notificación"
                showCount
                maxLength={500}
                onChange={(e) => setPreviewBody(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              name="imageUrl"
              label={
                <Space>
                  <PictureOutlined />
                  <span>Imagen (opcional)</span>
                </Space>
              }
            >
              <Input
                placeholder="https://ejemplo.com/imagen.jpg"
                onChange={(e) => setPreviewImage(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  <LinkOutlined />
                  <span>Vincular artículo (opcional)</span>
                </Space>
              }
            >
              <Select
                showSearch
                allowClear
                placeholder="Buscar artículo para vincular..."
                optionFilterProp="label"
                onChange={handleArticleSelect}
                value={selectedArticle?.id}
                style={{ width: '100%' }}
                options={articles
                  .filter(a => a.status === 'published')
                  .map(article => ({
                    value: article.id,
                    label: `${article.title} (${article.section})`,
                  }))
                }
              />
              {selectedArticle && (
                <Card
                  size="small"
                  style={{ marginTop: 8, background: '#f6ffed', borderColor: '#b7eb8f' }}
                >
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Text strong>{selectedArticle.title}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Sección: {selectedArticle.section}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Deep link: /{slugify(selectedArticle.section)}/{selectedArticle.id}/{slugify(selectedArticle.title)}
                    </Text>
                  </Space>
                </Card>
              )}
            </Form.Item>

            <Form.Item
              name="target"
              label="Destinatarios"
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="all">
                  <Space><MobileOutlined /> Todos los dispositivos</Space>
                </Select.Option>
                <Select.Option value="android">
                  <Space><AndroidOutlined style={{ color: '#3DDC84' }} /> Solo Android</Space>
                </Select.Option>
                <Select.Option value="ios">
                  <Space><AppleOutlined /> Solo iOS</Space>
                </Select.Option>
                <Select.Option value="web">
                  <Space><GlobalOutlined style={{ color: '#1890ff' }} /> Solo Web/PWA</Space>
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Tipo de envío">
              <Radio.Group
                value={sendType}
                onChange={(e) => setSendType(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                className="send-type-radio"
              >
                <Radio.Button value="immediate">
                  <Space size={4}><RocketOutlined /> Inmediato</Space>
                </Radio.Button>
                <Radio.Button value="scheduled">
                  <Space size={4}><ScheduleOutlined /> Programar</Space>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            {sendType === 'scheduled' && (
              <Form.Item
                name="scheduledAt"
                label="Fecha y hora de envío"
                rules={[{ required: true, message: 'Selecciona fecha y hora' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="DD/MM/YYYY HH:mm"
                  placeholder="Seleccionar fecha y hora"
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            )}

            <Divider />

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={sendType === 'scheduled' ? <ScheduleOutlined /> : <SendOutlined />}
                loading={sending}
                size="large"
                block
                style={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  background: sendType === 'scheduled' ? '#722ed1' : undefined,
                  borderColor: sendType === 'scheduled' ? '#722ed1' : undefined,
                }}
              >
                {sending
                  ? 'Enviando...'
                  : sendType === 'scheduled'
                    ? 'Programar Notificación'
                    : 'Enviar Notificación Ahora'
                }
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      {/* Vista previa */}
      <Col xs={24} lg={10}>
        <Card
          title={
            <Space>
              <MobileOutlined />
              <span>Vista previa</span>
            </Space>
          }
          style={{ position: 'sticky', top: 24 }}
        >
          <NotificationPreview
            title={previewTitle}
            body={previewBody}
            imageUrl={previewImage}
          />

          {stats && (
            <div style={{ marginTop: 24 }}>
              <Divider style={{ margin: '16px 0' }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                Se enviará a aproximadamente {' '}
                <Text strong style={{ fontSize: 12 }}>
                  {(() => {
                    const target = form.getFieldValue('target') || 'all';
                    if (target === 'all') return stats.devices?.total || 0;
                    return stats.devices?.[target] || 0;
                  })()}
                </Text>
                {' '} dispositivos activos.
              </Text>
            </div>
          )}
        </Card>

        {/* Imagen preview */}
        {previewImage && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              Vista previa de imagen:
            </Text>
            <img
              src={previewImage}
              alt="Imagen de la notificación"
              style={{
                width: '100%', maxHeight: 200, objectFit: 'cover',
                borderRadius: 8, border: '1px solid #f0f0f0'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Card>
        )}
      </Col>
    </Row>
  );

  const renderHistoryTab = () => (
    <Card
      title={
        <Space>
          <HistoryOutlined />
          <span>Historial de Notificaciones</span>
        </Space>
      }
      extra={
        <Space>
          <Select
            allowClear
            placeholder="Filtrar por estado"
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              loadNotifications(1, value);
            }}
          >
            <Select.Option value="sent">
              <Tag color="success">Enviadas</Tag>
            </Select.Option>
            <Select.Option value="scheduled">
              <Tag color="processing">Programadas</Tag>
            </Select.Option>
            <Select.Option value="cancelled">
              <Tag color="default">Canceladas</Tag>
            </Select.Option>
            <Select.Option value="failed">
              <Tag color="error">Fallidas</Tag>
            </Select.Option>
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadNotifications(notifPagination.page, statusFilter)}
          >
            Actualizar
          </Button>
        </Space>
      }
    >
      <Table
        columns={notificationColumns}
        dataSource={notifications}
        rowKey="id"
        loading={notifLoading}
        pagination={{
          current: notifPagination.page,
          pageSize: notifPagination.limit,
          total: notifPagination.total,
          showSizeChanger: false,
          showTotal: (total) => `${total} notificaciones`,
          onChange: (page) => loadNotifications(page, statusFilter),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No hay notificaciones aún"
            />
          ),
        }}
        scroll={{ x: 800 }}
      />
    </Card>
  );

  const renderDevicesTab = () => (
    <>
      {/* Estadísticas de dispositivos */}
      {stats?.devices && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={<Space><AndroidOutlined style={{ color: '#3DDC84' }} /> Android</Space>}
                value={stats.devices.android}
                valueStyle={{ color: '#3DDC84', fontWeight: 700 }}
              />
              {stats.devices.total > 0 && (
                <Progress
                  percent={Math.round((stats.devices.android / stats.devices.total) * 100)}
                  showInfo={false}
                  strokeColor="#3DDC84"
                  size="small"
                  style={{ marginTop: 8 }}
                />
              )}
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={<Space><AppleOutlined /> iOS</Space>}
                value={stats.devices.ios}
                valueStyle={{ color: '#262626', fontWeight: 700 }}
              />
              {stats.devices.total > 0 && (
                <Progress
                  percent={Math.round((stats.devices.ios / stats.devices.total) * 100)}
                  showInfo={false}
                  strokeColor="#262626"
                  size="small"
                  style={{ marginTop: 8 }}
                />
              )}
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title={<Space><GlobalOutlined style={{ color: '#1890ff' }} /> Web/PWA</Space>}
                value={stats.devices.web}
                valueStyle={{ color: '#1890ff', fontWeight: 700 }}
              />
              {stats.devices.total > 0 && (
                <Progress
                  percent={Math.round((stats.devices.web / stats.devices.total) * 100)}
                  showInfo={false}
                  strokeColor="#1890ff"
                  size="small"
                  style={{ marginTop: 8 }}
                />
              )}
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Total activos"
                value={stats.devices.total}
                valueStyle={{ color: '#722ed1', fontWeight: 700 }}
                prefix={<MobileOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabla de dispositivos */}
      <Card
        title={
          <Space>
            <TeamOutlined />
            <span>Dispositivos Registrados</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              allowClear
              placeholder="Filtrar plataforma"
              style={{ width: 160 }}
              value={platformFilter}
              onChange={(value) => {
                setPlatformFilter(value);
                loadDevices(1, value);
              }}
            >
              <Select.Option value="android">
                <Space><AndroidOutlined style={{ color: '#3DDC84' }} /> Android</Space>
              </Select.Option>
              <Select.Option value="ios">
                <Space><AppleOutlined /> iOS</Space>
              </Select.Option>
              <Select.Option value="web">
                <Space><GlobalOutlined style={{ color: '#1890ff' }} /> Web</Space>
              </Select.Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadDevices(devicesPagination.page, platformFilter)}
            >
              Actualizar
            </Button>
          </Space>
        }
      >
        <Table
          columns={deviceColumns}
          dataSource={devices}
          rowKey="id"
          loading={devicesLoading}
          pagination={{
            current: devicesPagination.page,
            pageSize: devicesPagination.limit,
            total: devicesPagination.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} dispositivos`,
            onChange: (page) => loadDevices(page, platformFilter),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No hay dispositivos registrados"
              />
            ),
          }}
          scroll={{ x: 700 }}
        />
      </Card>
    </>
  );

  const renderAutomationTab = () => (
    <>
      {/* Formulario para crear nueva regla */}
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>Notificación automática por sección</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Cuando se publique un artículo nuevo en una sección configurada, se enviará
          automáticamente una notificación con el título, subtítulo, imagen y deep link del artículo.
        </Text>

        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={8}>
            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Sección</Text>
            <Select
              showSearch
              placeholder="Seleccionar sección..."
              value={newRuleSection}
              onChange={setNewRuleSection}
              style={{ width: '100%' }}
              optionFilterProp="label"
              options={sections
                .filter(s => !autoRules.some(r => r.section === s))
                .map(s => ({ value: s, label: s }))
              }
            />
          </Col>
          <Col xs={12} sm={5}>
            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Destinatarios</Text>
            <Select
              value={newRuleTarget}
              onChange={setNewRuleTarget}
              style={{ width: '100%' }}
            >
              <Select.Option value="all">
                <Space><MobileOutlined /> Todos</Space>
              </Select.Option>
              <Select.Option value="android">
                <Space><AndroidOutlined style={{ color: '#3DDC84' }} /> Android</Space>
              </Select.Option>
              <Select.Option value="ios">
                <Space><AppleOutlined /> iOS</Space>
              </Select.Option>
              <Select.Option value="web">
                <Space><GlobalOutlined style={{ color: '#1890ff' }} /> Web</Space>
              </Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={5}>
            <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
              Hora de envío
            </Text>
            <TimePicker
              value={newRuleSendTime}
              onChange={setNewRuleSendTime}
              format="HH:mm"
              placeholder="Inmediato"
              style={{ width: '100%' }}
              minuteStep={1}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateAutoRule}
              block
              style={{ marginTop: 4 }}
            >
              Añadir regla
            </Button>
          </Col>
        </Row>
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <InfoCircleOutlined style={{ marginRight: 4 }} />
            Si no se selecciona hora, la notificación se envía en cuanto se publique el artículo.
            Si se indica una hora, se programará para esa hora del día.
          </Text>
        </div>
      </Card>

      {/* Lista de reglas activas */}
      <Card
        title={
          <Space>
            <ScheduleOutlined />
            <span>Reglas configuradas</span>
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={loadAutoRules}>
            Actualizar
          </Button>
        }
      >
        <Table
          dataSource={autoRules}
          rowKey="id"
          loading={autoRulesLoading}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No hay reglas configuradas. Añade una sección para enviar notificaciones automáticas."
              />
            ),
          }}
          columns={[
            {
              title: 'Sección',
              dataIndex: 'section',
              key: 'section',
              render: (section) => (
                <Text strong style={{ fontSize: 15 }}>{section}</Text>
              ),
            },
            {
              title: 'Destinatarios',
              dataIndex: 'target',
              key: 'target',
              width: 140,
              render: (target) => (
                <Space size={4}>
                  {platformIcons[target]}
                  <span>{platformLabels[target]}</span>
                </Space>
              ),
            },
            {
              title: 'Hora de envío',
              dataIndex: 'send_time',
              key: 'send_time',
              width: 130,
              render: (sendTime) => (
                sendTime
                  ? <Tag icon={<ClockCircleOutlined />} color="blue">{sendTime.substring(0, 5)}</Tag>
                  : <Tag color="default">Inmediato</Tag>
              ),
            },
            {
              title: 'Estado',
              dataIndex: 'enabled',
              key: 'enabled',
              width: 120,
              render: (enabled, record) => (
                <Tag
                  color={enabled ? 'success' : 'default'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleToggleAutoRule(record)}
                >
                  {enabled ? 'Activa' : 'Pausada'}
                </Tag>
              ),
            },
            {
              title: 'Creada',
              dataIndex: 'created_at',
              key: 'created_at',
              width: 140,
              render: (date) => (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {dayjs(date).format('DD MMM YYYY')}
                </Text>
              ),
            },
            {
              title: 'Acciones',
              key: 'actions',
              width: 160,
              render: (_, record) => (
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleOpenEditRule(record)}
                  >
                    Editar
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleToggleAutoRule(record)}
                  >
                    {record.enabled ? 'Pausar' : 'Activar'}
                  </Button>
                  <Popconfirm
                    title={`¿Eliminar la regla para "${record.section}"?`}
                    onConfirm={() => handleDeleteAutoRule(record.id)}
                    okText="Sí, eliminar"
                    cancelText="No"
                  >
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                      Eliminar
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      {/* Modal de edición de regla */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>Editar regla: {editingRule?.section}</span>
          </Space>
        }
        open={!!editingRule}
        onCancel={() => setEditingRule(null)}
        onOk={handleSaveEditRule}
        confirmLoading={editSaving}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Destinatarios</Text>
            <Select
              value={editTarget}
              onChange={setEditTarget}
              style={{ width: '100%' }}
            >
              <Select.Option value="all">
                <Space><MobileOutlined /> Todos</Space>
              </Select.Option>
              <Select.Option value="android">
                <Space><AndroidOutlined style={{ color: '#3DDC84' }} /> Android</Space>
              </Select.Option>
              <Select.Option value="ios">
                <Space><AppleOutlined /> iOS</Space>
              </Select.Option>
              <Select.Option value="web">
                <Space><GlobalOutlined style={{ color: '#1890ff' }} /> Web</Space>
              </Select.Option>
            </Select>
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Hora de envío</Text>
            <TimePicker
              value={editSendTime}
              onChange={setEditSendTime}
              format="HH:mm"
              placeholder="Inmediato"
              style={{ width: '100%' }}
              minuteStep={1}
              allowClear
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              Dejar vacío para envío inmediato al publicar.
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );

  // ============================================
  // Render principal
  // ============================================

  if (loading) {
    return (
      <div style={{
        padding: '50px', textAlign: 'center', minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'
      }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Cargando notificaciones...</p>
      </div>
    );
  }

  return (
    <div className="notifications-page" style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <BellOutlined style={{ fontSize: 28, color: '#c82318' }} />
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Notificaciones Push
            </Title>
            <Text type="secondary">
              Envía notificaciones a los dispositivos de la app Agustinos
            </Text>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card hoverable style={{ textAlign: 'center' }}>
              <Statistic
                title="Dispositivos"
                value={stats.devices?.total || 0}
                prefix={<MobileOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card hoverable style={{ textAlign: 'center' }}>
              <Statistic
                title="Enviadas"
                value={stats.notifications?.sent || 0}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card hoverable style={{ textAlign: 'center' }}>
              <Statistic
                title="Programadas"
                value={stats.notifications?.scheduled || 0}
                prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card hoverable style={{ textAlign: 'center' }}>
              <Statistic
                title="Fallidas"
                value={stats.notifications?.failed || 0}
                prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabs principales */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        size="large"
        className="notifications-tabs cms-tabs-grid"
        items={[
          {
            key: 'send',
            label: (
              <Space>
                <SendOutlined />
                Enviar Notificación
              </Space>
            ),
            children: renderSendTab(),
          },
          {
            key: 'history',
            label: (
              <Space>
                <HistoryOutlined />
                Historial
                {stats?.notifications?.scheduled > 0 && (
                  <Badge count={stats.notifications.scheduled} size="small" />
                )}
              </Space>
            ),
            children: renderHistoryTab(),
          },
          {
            key: 'devices',
            label: (
              <Space>
                <TeamOutlined />
                Dispositivos
                {stats?.devices?.total > 0 && (
                  <Badge count={stats.devices.total} size="small" overflowCount={9999} />
                )}
              </Space>
            ),
            children: renderDevicesTab(),
          },
          {
            key: 'automation',
            label: (
              <Space>
                <ThunderboltOutlined />
                Automatizaciones
                {autoRules.filter(r => r.enabled).length > 0 && (
                  <Badge count={autoRules.filter(r => r.enabled).length} size="small" color="#722ed1" />
                )}
              </Space>
            ),
            children: renderAutomationTab(),
          },
        ]}
      />

      {/* Estilos responsive */}
      <style jsx="true">{`
        @media (max-width: 768px) {
          .ant-tabs-nav-list {
            flex-wrap: nowrap;
          }
          .ant-tabs-tab {
            padding: 8px 12px !important;
            font-size: 13px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Notifications;
